'use client';

import { cn, Div, Lottie, Span } from '@scatterlab/zeta-core';
import { FC, memo, ReactNode, useMemo } from 'react';

import { BUBBLE_THEME_CLASS_NAMES, BubbleTheme } from './bubble-theme';
import BubbleMarkdown from './BubbleMarkdown';
import ChatBubbleContainer from './ChatBubbleContainer';
import loading from '../../assets/animations/loading.json';

export type ChatBubbleProps = {
  message: string;
  isMine?: boolean;
  className?: string;
  isLoading?: boolean;
  containerClassName?: string;
  translucent?: boolean;
  placeholder?: string;
  onPress?: () => void;
  onLongPress?: () => void;
  rounded?: boolean;
  isLinkAllowed?: boolean;
  ttsChip?: ReactNode;
  theme?: BubbleTheme;
  markdownEnabled?: boolean;
  smallText?: boolean;
};

const ChatBubble: FC<ChatBubbleProps> = ({
  message,
  isMine,
  isLoading,
  className,
  containerClassName,
  translucent,
  placeholder,
  onPress,
  onLongPress,
  rounded,
  isLinkAllowed,
  ttsChip,
  theme = BubbleTheme.DEFAULT,
  markdownEnabled = true,
  smallText,
}) => {
  const bubbleElement = useMemo(() => {
    if (isLoading)
      return (
        <Lottie
          loop
          animationData={loading}
          autoPlay
          className="mx-3 my-2 h-[19.3px] w-[42px]"
        />
      );

    const textClassName =
      BUBBLE_THEME_CLASS_NAMES[theme][isMine ? 'mine' : 'character'].text;

    if (!markdownEnabled)
      return (
        <Div
          className={cn(
            'body16 whitespace-pre-wrap break-all',
            theme === BubbleTheme.DEFAULT ? 'text-gray-200' : 'opacity-70',
            textClassName,
            smallText && 'text-[15px]',
          )}
        >
          {message.trim() || '\u00A0'}
        </Div>
      );

    return (
      <BubbleMarkdown
        message={message.trim() || '\u00A0'}
        className={cn(textClassName, className, smallText && 'text-[15px]')}
        isLinkAllowed={isLinkAllowed}
        theme={theme}
        isMine={isMine}
      />
    );
  }, [
    isLoading,
    theme,
    isMine,
    message,
    className,
    isLinkAllowed,
    markdownEnabled,
    smallText,
  ]);

  return (
    <ChatBubbleContainer
      className={containerClassName}
      mine={isMine}
      loading={isLoading}
      translucent={translucent}
      onPress={onPress}
      onLongPress={onLongPress}
      rounded={rounded}
      theme={theme}
    >
      {ttsChip}
      {bubbleElement}
      {placeholder && !message && (
        <Span className="body16 text-white/20">{placeholder}</Span>
      )}
    </ChatBubbleContainer>
  );
};

export default memo(ChatBubble);
